import React, { useEffect, useState, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { SegmentedSlider } from '@ondeck/silkworm';
import { TermDetail, ModalType, Terms } from 'states/initialState';
import { IntegerFormat, MoneyFormat } from '../Common/TextFormat';
import { useTracking } from 'react-tracking';
import debounce from 'lodash.debounce';
import { Row, Stack, Button, ButtonGroup, ToggleButton, Spinner } from 'react-bootstrap';
import { InputSliderCombo } from 'components/Common/InputSliderCombo';

interface CalcProps {
  onlineCheckoutEligible: boolean;
  businessId: string;
  offer: TermDetail;
  setDealChanged: any;
  handlePriceDeal: any;
  setFixedCommission: any;
  fixedCommission: boolean;
}

const choices = ['Daily', 'Weekly'];
const MAX_DISCOUNT = 100;
const NORMAL_DISCOUNT = 25;

/* istanbul ignore next */ 
const maxBy = (arr, key) => arr.reduce((a, b) => (a[key] >= b[key] ? a : b), {});
const pluck = (objs:object[], property:string):any[] => objs.map((obj) => obj[property]);
const findDuplicates = arr => arr.filter((item, index) => arr.indexOf(item) !== index);
const findObjectByValue = (obj, att:string, val:any) => obj.filter(obj => obj[att] === val);
const findValidTerms = (terms: Terms[], loamAmount: string | number) => terms.filter((term) => term.lineAmount >= Number(loamAmount));

const Calculator = ({
  onlineCheckoutEligible,
  businessId,
  offer,
  setDealChanged,
  handlePriceDeal,
  setFixedCommission,
  fixedCommission
}: CalcProps) => {
  const MIN_VALUE = 5001;
  const { terms, maxLineAmount} = offer;
  const [termOffer, setTermOffer] = useState(offer.terms[0]);
  const [validTerms, setValidTerms]= useState(offer.terms);
  const [selectedOfferID, setSelectedOfferID] = useState('');
  const { sellRate, buyRate } = termOffer;
  const [radioValue, setRadioValue] = useState(choices[1]);
  const [maxCommission, setMaxCommission] = useState(10);
  const [commissionPoints, setCalcCommissionPoints] = useState(10);
  const [duplicateTerms, setDuplicateTerms] = useState<Terms[]>([]);
  const [discountPercentage, setDiscountPercentage] = useState(NORMAL_DISCOUNT);
  const [canPrice, setCanPrice] = useState(false);
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [loanAmt, setLoanAmt] = useState(maxLineAmount.toString());
  const { formatMessage } = useIntl();
  const { trackEvent } = useTracking();

  const computeSelectedDeal = () => {
    const commissionAmount = +(+loanAmt * (commissionPoints / 100)).toFixed(2);
    return {
      offerId: termOffer.offerId,
      loanSpecifics: [
        {
          collectionType: 'ACH',
          line: +loanAmt,
          paymentFrequency: radioValue,
          requestedCommission: commissionPoints,
          term: +termOffer.term,
          centsOnDollar: +termOffer.buyRate + commissionPoints / 100,
          productType: ModalType.TL,
          prepaymentDiscountPercentage: termOffer.prepaymentDiscountPercentage
        }
      ],
      commissionAmount: commissionAmount,
      commissionPoints: commissionPoints
    };
  };

  const onSubmit = async () => {
    setLoadingPricing(true);
    const selectedDeal = computeSelectedDeal();
    await handlePriceDeal(selectedDeal);
    trackEvent({
      event: 'Price Deal',
      opportunityId: businessId,
      loanAmount: selectedDeal.loanSpecifics[0].line,
      paymentFrequency: selectedDeal.loanSpecifics[0].paymentFrequency,
      term: selectedDeal.loanSpecifics[0].term,
      commissionPoints: commissionPoints,
      onlineCheckoutEligible: onlineCheckoutEligible,
      maxLoanAmount: maxLineAmount
    });
    setLoadingPricing(false);
  };

  useEffect(() => {
    const termLengths = pluck(terms, 'term');
    const duplicateTermLengths = findDuplicates(termLengths);
    setDuplicateTerms(
      terms.filter(t => duplicateTermLengths.includes(t.term))
    );
  }, [terms]);

  useEffect(() => {
    setValidTerms(findValidTerms(terms, loanAmt));
  }, [terms, loanAmt]);

  useEffect(() => {
    if(findObjectByValue(validTerms, 'offerId', selectedOfferID).length === 0) {
      setSelectedOfferID('');
    }
  }, [validTerms]);

  useEffect(() => {
    setFixedCommission(sellRate === buyRate);
    setMaxCommission(Math.round(100 * Math.abs(sellRate - buyRate)));
  }, [sellRate, buyRate]);

  useEffect(() => {
    commissionPoints > maxCommission && setCalcCommissionPoints(maxCommission);
  }, [maxCommission]);

  useEffect(() => {
    setDealChanged(true);
    debouncedEvent(() => {
      const selectedDeal = computeSelectedDeal();
      trackEvent({
        event: 'Deal Updated',
        opportunityId: businessId,
        loanAmount: loanAmt,
        paymentFrequency: selectedDeal.loanSpecifics[0].paymentFrequency,
        term: selectedDeal.loanSpecifics[0].term,
        commissionPoints: commissionPoints
      });
    });
  }, [termOffer, radioValue, commissionPoints, loanAmt]);

  useEffect(() => {
    if (selectedOfferID === '') { setCanPrice(false) } else {
      setCanPrice(true)
      let term = findObjectByValue(terms, 'offerId', selectedOfferID)[0];
      setTermOffer(term);
      if (duplicateTerms.includes(term)) {
        const sameTerms = duplicateTerms.filter(t => t.term === term.term);
        const highestCODTerm = maxBy(sameTerms, 'centsOnDollar');
        highestCODTerm === term ? setDiscountPercentage(MAX_DISCOUNT) : setDiscountPercentage(NORMAL_DISCOUNT);
      } else {
        setDiscountPercentage(NORMAL_DISCOUNT);
      }
    }
  }, [selectedOfferID]);

  const debouncedEvent = useCallback(
    debounce((trigger: Function) => {
      trigger();
    }, 1000),
    []
  );

  return (
    <div className="fs-5">
      <div className="h2 fw-400 text-md-start text-center">
        <FormattedMessage id="calculator.heading" />
      </div>
      <Stack className="col pt-2" gap={2}>
        <FormattedMessage id="calculator.termAmt" />
        <InputSliderCombo
          min={MIN_VALUE}
          max={maxLineAmount}
          onChange={v => setLoanAmt(v) }
          value={Number(loanAmt)}
        />
      </Stack>
      {/* Soft-removed for OG-467. See https://agile.enova.com/browse/OG-516 for the follow up ticket
      <Stack direction="horizontal" gap={2}>
        <FormattedMessage id="calculator.crossSell" />
        <span className="text-danger">
          <MoneyFormat value={maxLineAmount * 0.6} variant="danger" />{' '}
          <FormattedMessage id="calculator.orLess" />
        </span>
      </Stack> */}
      <Row sm={1} md={2} className="py-5">
        <Stack gap={2}>
          <FormattedMessage id="calculator.paymentFreq" />
          <ButtonGroup role="form" size="lg" className="mb-2">
            {choices.map(c => (
              <ToggleButton
                className="text-capitalize"
                type="radio"
                key={c}
                id={`radio-${c}`}
                data-testid={`radio-${c}`}
                variant={'outline-accent'}
                name={c}
                value={c}
                checked={radioValue === c}
                onChange={() => setRadioValue(c)}
              >
                {c}
              </ToggleButton>
            ))}
          </ButtonGroup>
        </Stack>
        <Stack gap={2}>
          <FormattedMessage id="calculator.term" />
          <select
            className="form-select form-select-lg"
            data-testid="termLength"
            onChange={e => setSelectedOfferID(e.target.value)}
          >
            <option value="">(Select a term)</option>
            {validTerms.map((t) => (
              <option key={t.offerId} value={t.offerId}>
                {t.term} {formatMessage({ id: 'calculator.months' })}{' '}
                {duplicateTerms.length !== 0 && `@ ${t.centsOnDollar} COD`}
              </option>
            ))}
          </select>
        </Stack>
      </Row>
      {!fixedCommission && (
        <Row>
          <Stack className="mb-3" direction="horizontal" gap={2}>
            <FormattedMessage id="calculator.commission" />:
            <IntegerFormat value={commissionPoints} />
          </Stack>
          <SegmentedSlider
            min={0}
            max={maxCommission}
            disabled={!loanAmt}
            value={commissionPoints}
            onChangeEvt={e => {
              setCalcCommissionPoints(e);
            }}
          />
          <Stack className="mt-3" direction="horizontal" gap={2}>
            <Stack direction="horizontal" gap={1}>
              <FormattedMessage id="calculator.buyRate" />
              <MoneyFormat value={buyRate} decimalScale={3} toFixed={false} variant="text" />
            </Stack>
            <Stack className="ms-auto" direction="horizontal" gap={1}>
              <FormattedMessage id="calculator.sellRate" />
              <MoneyFormat value={sellRate} decimalScale={3} toFixed={false} variant="text" />
            </Stack>
          </Stack>
        </Row>
      )}
      <hr />
      <Stack className="mt-4" direction="horizontal" gap={5}>
        <Stack direction="horizontal" gap={2}>
          <FormattedMessage id="calculator.discount" />
          <IntegerFormat value={discountPercentage} suffix="%" />
        </Stack>
        <Stack direction="horizontal" gap={2}>
          <FormattedMessage id="calculator.renewal" />
          <span className="text-secondary">
            <FormattedMessage id="calculator.renewal.percentage" />
          </span>
        </Stack>
      </Stack>
      <Row>
        <Button
          className="col-md-3 mt-4 ms-auto"
          type="submit"
          data-testid="submit"
          disabled={!canPrice || loadingPricing}
          onClick={() => onSubmit()}
        >
          {loadingPricing ? (
            <span className='me-1'>
              <Spinner animation="border" size='sm' className='me-2'/>
              Pricing...
            </span>
          ) : (
            <FormattedMessage id="calculator.submit" />
          )}
        </Button>
      </Row>
    </div>
  );
};

export default Calculator;
